// **React Imports
import React, {
  Suspense,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import {
  matchPath,
  Navigate,
  Outlet,
  useLocation,
  useParams
} from 'react-router-dom'

// **MUI Imports
import { Box, styled } from '@mui/material'

// **Component Imports
import Header from '../components/header'
import OverlayLoading from '../components/overlay-loading'
import ErrorBoundary from '../pages/error'

// **Context Imports
import { SettingsContext } from '../context/setting'

// **Utils Imports
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import '../'
import Breadcrumbs from '../components/breadcrumbs'
import CandidateDetailsModal from '../components/candidate-detail-modal'
import LinkedinRequiredCard from '../components/linkedin-required-card'
import CandidateSidepanel from '../pages/candidate/components/candidate-sidepanel'
import PracticeModeLoading from '../pages/candidate/home/practice/PracticeModeLoading'
import { setCandidateAppMode } from '../store/slice/candidate'
import { CANDIDATE_APP_MODE, PREP_MODE_PATHS } from '../utils/constant'
import {
  checkCandidateAccess,
  getUserDetails,
  removeJWTToken
} from '../utils/utils'

// **Styled Component
const MainContainer = styled('main')(({ theme, contentWidth }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',

  ...(contentWidth === 'boxed'
    ? {
        margin: '0 auto',
        maxWidth: '1440px'
      }
    : {}),
  [theme.breakpoints.up('md')]: {
    padding: '24px 16px'
  },
  [theme.breakpoints.down('md')]: {
    padding: '16px 8px'
  }
}))

const CandidatePrivateRoute = () => {
  // hooks
  const { contestUri } = useParams()
  const {
    settings: { contentWidth, appBarHidden, removePadding }
  } = useContext(SettingsContext)
  const location = useLocation()

  // redux hooks
  const candidateAppMode = useSelector(
    (state) => state.candidate.candidateAppMode
  )
  // variable
  const globalCandidatePath = useMemo(() => {
    const path = location.pathname?.trim()?.toLowerCase()
    return path.endsWith('/c') || path.endsWith('/c/') || path.includes('/c/')
  }, [location.pathname])

  const isPrepMode = useMemo(() => {
    return candidateAppMode === CANDIDATE_APP_MODE.PRACTICE
  }, [candidateAppMode])

  const dispatch = useDispatch()

  const isPracticeOnlyRoute = useMemo(() => {
    const practicePaths = PREP_MODE_PATHS?.slice(2) || []
    return practicePaths.some((pattern) =>
      matchPath({ path: pattern, end: false }, location.pathname)
    )
  }, [location.pathname])

  const isLinkedInRequiredRoute = useMemo(() => {
    const path = location.pathname?.trim()?.toLowerCase()

    if (isPrepMode && (path === '/c' || path === '/c/')) {
      return true
    }

    return isPracticeOnlyRoute
  }, [location.pathname, isPrepMode, isPracticeOnlyRoute])

  // Automatically switch mode from Prep to Interview if visiting a contest URL
  useEffect(() => {
    if (isPrepMode && contestUri) {
      dispatch(setCandidateAppMode(CANDIDATE_APP_MODE.INTERVIEW))
      toast.info('Switched to Interview Mode', {
        toastId: 'switch-interview-mode'
      })
    }
  }, [isPrepMode, contestUri, dispatch])

  const isLinkedInVerified = useMemo(() => {
    const details = getUserDetails()
    return !!details?.isLinkedinVerified
  }, [])

  // states
  const [isPrepSwitching, setIsPrepSwitching] = useState(false)
  const prevModeRef = useRef(candidateAppMode)

  // effect
  useEffect(() => {
    if (
      prevModeRef.current !== CANDIDATE_APP_MODE.PRACTICE &&
      candidateAppMode === CANDIDATE_APP_MODE.PRACTICE
    ) {
      setIsPrepSwitching(true)
      const timer = setTimeout(() => {
        setIsPrepSwitching(false)
      }, 2000)
      return () => clearTimeout(timer)
    }
    prevModeRef.current = candidateAppMode
  }, [candidateAppMode])

  if (checkCandidateAccess()) {
    if (isPrepSwitching) {
      return <PracticeModeLoading />
    }

    if (isPracticeOnlyRoute && !isPrepMode) {
      toast.warning(
        'Please switch to Practice Mode to access practice features.',
        { toastId: 'practice-mode-redirect' }
      )
      return <Navigate to="/c" replace />
    }

    return (
      <React.Fragment>
        <Box
          className="candidate-private-route-wrapper-container"
          sx={{ height: '100%', width: '100%', display: 'flex' }}
        >
          {isPrepMode && <CandidateSidepanel />}
          <Box
            className="candidate-private-route-main-container-wrapper"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              height: '100%',
              overflowY: 'auto',
              transition: 'width 0.3s ease',
              flex: 1
            }}
          >
            {!appBarHidden && <Header candidatePathHeader={true} />}
            <Breadcrumbs />
            <ErrorBoundary>
              <Suspense fallback={<OverlayLoading />}>
                <MainContainer
                  contentWidth={contentWidth}
                  sx={{ ...(removePadding ? { padding: '0 !important' } : {}) }}
                  className="candidate-private-route-main-container"
                >
                  {isLinkedInRequiredRoute && !isLinkedInVerified ? (
                    <LinkedinRequiredCard />
                  ) : (
                    <Outlet />
                  )}
                </MainContainer>
              </Suspense>
            </ErrorBoundary>
            <CandidateDetailsModal />
          </Box>
        </Box>
      </React.Fragment>
    )
  } else {
    removeJWTToken()

    return contestUri ? (
      <Navigate to={`/c/${contestUri}/login`} replace />
    ) : globalCandidatePath ? (
      <Navigate to={'/candidates/login'} replace />
    ) : (
      <Navigate to={'/login'} replace />
    )
  }
}

export default CandidatePrivateRoute
